/*
 * SH - Compatible filter
 * Loads all grid pages, sorts compatible products first, paginates client-side.
 * Re-applies after each theme grid reload (#facet-main).
 */
(() => {
  if (customElements.get('sh-compatible-filter')) return;

  const HIDDEN_CLASS = 'sh-compat-hidden';
  const LOADING_CLASS = 'sh-compat-loading';
  const MAX_PARALLEL_REQUESTS = 6;

  class ShCompatibleFilter extends HTMLElement {
    connectedCallback() {
      this.facet = document.querySelector('product-facet');
      this.main = this.facet ? this.facet.querySelector('#facet-main') : null;

      this.toggle = this.querySelector('[data-sh-cf-toggle]');
      this.panel = this.querySelector('[data-sh-cf-options]');
      this.valueEl = this.querySelector('[data-sh-cf-value]');
      this.onlyInput = this.querySelector('[data-sh-cf-only]');
      this.counterEl = this.querySelector('[data-sh-cf-counter]');
      this.brandInputs = Array.from(this.querySelectorAll('[data-sh-cf-brand]'));
      this.paginationTemplate = this.querySelector('[data-sh-cf-pagination-template]');
      this.pageLabelTemplate = this.querySelector('[data-sh-cf-page-label]');

      this.placeholder = this.dataset.placeholder || '';
      this.counterText = this.dataset.counterText || '[count] / [total]';
      this.cache = new Map();
      this.originalCards = null;
      this.orderedCards = null;
      this.page = 1;
      this.requestId = 0;

      this.onToggleClick = () => this.setOpen(!this.isOpen);
      this.onDocumentClick = (event) => {
        if (this.isOpen && !this.contains(event.target)) this.setOpen(false);
      };
      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          this.setOpen(false);
          this.toggle.focus();
        }
      };
      this.onBrandChange = () => {
        this.updateControls();
        this.page = 1;
        this.apply();
      };
      this.onOnlyChange = () => {
        this.page = 1;
        this.apply();
      };

      this.toggle.addEventListener('click', this.onToggleClick);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
      this.brandInputs.forEach((input) => input.addEventListener('change', this.onBrandChange));
      this.onlyInput.addEventListener('change', this.onOnlyChange);

      if (this.main) {
        // Skip our own pagination changes.
        const isOwnNode = (node) => node.nodeType === 1 && node.classList.contains('sh-compat-pagination');
        this.observer = new MutationObserver((mutations) => {
          const replacedByTheme = mutations.some((mutation) =>
            [...mutation.addedNodes, ...mutation.removedNodes].some((node) => !isOwnNode(node))
          );
          if (replacedByTheme) this.onGridReplaced();
        });
        this.observer.observe(this.main, { childList: true });
      }

      this.updateControls();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      if (this.observer) this.observer.disconnect();

      // Theme editor reload: restore the grid.
      this.requestId++;
      const list = this.getList();
      if (list) this.restore(list);
    }

    get isOpen() {
      return this.toggle.getAttribute('aria-expanded') === 'true';
    }

    get selectedKeys() {
      return this.brandInputs.filter((input) => input.checked).map((input) => input.value);
    }

    setOpen(open) {
      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.panel.hidden = !open;
      this.classList.toggle('is-open', open);
    }

    updateControls() {
      const checked = this.brandInputs.filter((input) => input.checked);

      this.brandInputs.forEach((input) => {
        input.closest('label').classList.toggle('is-checked', input.checked);
      });

      this.valueEl.textContent = checked.length
        ? checked.map((input) => input.dataset.name).join(', ')
        : this.placeholder;
      this.valueEl.classList.toggle('is-placeholder', checked.length === 0);

      this.onlyInput.disabled = checked.length === 0;
      if (checked.length === 0) this.onlyInput.checked = false;
    }

    /* Grid helpers */

    getList() {
      return this.main ? this.main.querySelector('.product-list__inner') : null;
    }

    getProductList(list) {
      return list.closest('product-list');
    }

    getCards(list) {
      return Array.from(list.children).filter((child) => child.tagName === 'PRODUCT-ITEM');
    }

    getCardKeys(card) {
      if (!card.shCompatKeys) {
        const badges = card.querySelector('.sh-compat-badges');
        card.shCompatKeys = badges ? badges.dataset.shCompat.split(' ').filter(Boolean) : [];
      }
      return card.shCompatKeys;
    }

    isCompatible(card, selected) {
      return this.getCardKeys(card).some((key) => selected.has(key));
    }

    updateBadges(card, selected) {
      const badges = card.querySelector('.sh-compat-badges');
      if (!badges) return;

      // ✕ badges only show next to at least one ✓ badge.
      let hasMatch = false;
      badges.querySelectorAll('[data-sh-brand]').forEach((badge) => {
        const show = selected.has(badge.dataset.shBrand);
        badge.hidden = !show;
        if (show && !badge.hasAttribute('data-sh-miss')) hasMatch = true;
      });
      badges.hidden = !hasMatch;
    }

    /* Show exactly these cards, in this order. */
    showCards(list, cards) {
      const keep = new Set(cards);
      this.getCards(list).forEach((card) => {
        if (!keep.has(card)) card.remove();
      });

      const fragment = document.createDocumentFragment();
      cards.forEach((card) => {
        card.classList.remove(HIDDEN_CLASS);
        fragment.appendChild(card);
      });
      list.appendChild(fragment);
    }

    setThemePaginationHidden(hidden) {
      const pagination = this.main && this.main.querySelector('page-pagination');
      if (pagination) pagination.classList.toggle(HIDDEN_CLASS, hidden);
    }

    setProductCount(count) {
      const countEl = this.main && this.main.querySelector('.product-facet__meta-bar-item--count');
      if (!countEl) return;

      if (countEl.dataset.shOriginal === undefined) countEl.dataset.shOriginal = countEl.textContent;
      countEl.textContent = count === null
        ? countEl.dataset.shOriginal
        : countEl.dataset.shOriginal.replace(/\d+/, count);
    }

    setCounter(count, total) {
      this.counterEl.textContent = count === null
        ? ''
        : this.counterText.replace('[count]', count).replace('[total]', total);
    }

    setLoading(list, loading) {
      const productList = this.getProductList(list);
      if (productList) productList.classList.toggle(LOADING_CLASS, loading);
    }

    /* Main logic */

    onGridReplaced() {
      // New theme grid: old cards are gone.
      this.originalCards = null;
      this.orderedCards = null;
      this.page = 1;
      if (this.selectedKeys.length) this.apply();
    }

    async apply() {
      const list = this.getList();
      const requestId = ++this.requestId;
      const selected = new Set(this.selectedKeys);

      if (!list) {
        this.setCounter(selected.size ? 0 : null, 0);
        return;
      }

      if (!this.originalCards) this.originalCards = this.getCards(list);

      if (!selected.size) {
        this.restore(list);
        return;
      }

      const result = await this.getAllCards(list, requestId);
      if (requestId !== this.requestId || list !== this.getList()) return;

      this.render(list, result.cards, selected, result.complete);
    }

    async getAllCards(list, requestId) {
      const productList = this.getProductList(list);
      const pages = parseInt((productList && productList.dataset.shPages) || '1', 10);

      if (pages <= 1 || !this.facet) return { cards: this.originalCards, complete: true };

      const key = this.getCacheKey();
      if (!this.cache.has(key)) {
        const request = this.fetchAllCards(pages).catch((error) => {
          this.cache.delete(key);
          throw error;
        });
        this.cache.set(key, request);
      }

      this.setLoading(list, true);
      try {
        return { cards: await this.cache.get(key), complete: true };
      } catch (error) {
        // Fallback: current page only.
        return { cards: this.originalCards, complete: false };
      } finally {
        if (requestId === this.requestId) this.setLoading(list, false);
      }
    }

    getCacheKey() {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      url.searchParams.sort();
      return url.pathname + '?' + url.searchParams.toString();
    }

    async fetchAllCards(pages) {
      const sectionId = this.facet.getAttribute('section-id');
      const htmlByPage = new Array(pages);
      const queue = Array.from({ length: pages }, (_, index) => index + 1);

      const fetchPage = async (page) => {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', sectionId);
        url.searchParams.set('page', page);

        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`Page ${page}: ${response.status}`);
        htmlByPage[page - 1] = await response.text();
      };

      // Limit parallel requests.
      const workers = Array.from({ length: Math.min(MAX_PARALLEL_REQUESTS, pages) }, async () => {
        while (queue.length) await fetchPage(queue.shift());
      });
      await Promise.all(workers);

      const cards = [];
      htmlByPage.forEach((html) => {
        const container = document.createElement('div');
        container.innerHTML = html;
        container.querySelectorAll('#facet-main .product-list__inner > product-item').forEach((card) => {
          // [reveal] stays hidden without the theme animation.
          card.removeAttribute('reveal');
          cards.push(card);
        });
      });

      return cards;
    }

    render(list, cards, selected, complete) {
      const onlyCompatible = this.onlyInput.checked;
      const compatible = [];
      const others = [];

      cards.forEach((card) => {
        this.updateBadges(card, selected);
        (this.isCompatible(card, selected) ? compatible : others).push(card);
      });

      this.orderedCards = onlyCompatible ? compatible : compatible.concat(others);
      this.setCounter(compatible.length, cards.length);

      if (complete) {
        this.setProductCount(this.orderedCards.length);
        this.setThemePaginationHidden(true);
        this.renderPage(list);
      } else {
        // Fallback: keep the theme pagination.
        this.setProductCount(null);
        this.removePagination(list);
        this.showCards(list, this.orderedCards);
      }
    }

    /* Client pagination */

    getPageSize(list) {
      const productList = this.getProductList(list);
      const size = parseInt((productList && productList.dataset.shPerPage) || '0', 10);
      return size > 0 ? size : this.orderedCards.length || 1;
    }

    renderPage(list, scroll = false) {
      const pageSize = this.getPageSize(list);
      const pageCount = Math.max(1, Math.ceil(this.orderedCards.length / pageSize));
      this.page = Math.min(Math.max(1, this.page), pageCount);

      const start = (this.page - 1) * pageSize;
      this.showCards(list, this.orderedCards.slice(start, start + pageSize));
      this.renderPagination(list, pageCount);

      if (scroll) {
        const target = this.main.querySelector('.product-facet__meta-bar') || this.getProductList(list);
        if (target) requestAnimationFrame(() => target.scrollIntoView({ block: 'start', behavior: 'smooth' }));
      }
    }

    removePagination(list) {
      const productList = this.getProductList(list);
      const existing = productList && productList.parentElement.querySelector('.sh-compat-pagination');
      if (existing) existing.remove();
    }

    /* Like paginate.parts: 1 … 4 5 6 … 10 */
    getPageParts(pageCount) {
      const parts = [];
      let previous = 0;

      for (let page = 1; page <= pageCount; page++) {
        if (page === 1 || page === pageCount || Math.abs(page - this.page) <= 1) {
          if (page - previous > 1) parts.push(null);
          parts.push(page);
          previous = page;
        }
      }
      return parts;
    }

    renderPagination(list, pageCount) {
      this.removePagination(list);
      if (pageCount <= 1 || !this.paginationTemplate) return;

      const productList = this.getProductList(list);
      const pagination = this.paginationTemplate.content.firstElementChild.cloneNode(true);
      const nav = pagination.querySelector('.pagination__nav');
      const prev = nav.querySelector('[data-sh-cf-prev]');
      const next = nav.querySelector('[data-sh-cf-next]');
      const pageLabel = this.pageLabelTemplate ? this.pageLabelTemplate.innerHTML.trim() : '[page]';

      this.getPageParts(pageCount).forEach((page) => {
        let item;

        if (page === null) {
          item = document.createElement('span');
          item.textContent = '…';
        } else if (page === this.page) {
          item = document.createElement('span');
          item.setAttribute('aria-current', 'page');
          item.textContent = page;
        } else {
          item = document.createElement('a');
          item.href = '#';
          item.dataset.page = page;
          item.setAttribute('aria-label', pageLabel.replace('[page]', page));
          item.textContent = page;
        }

        item.className = 'pagination__nav-item heading heading--small';
        nav.insertBefore(item, next);
      });

      // Like the theme: no arrow past the ends.
      if (this.page === 1) prev.remove();
      else prev.dataset.page = this.page - 1;

      if (this.page === pageCount) next.remove();
      else next.dataset.page = this.page + 1;

      nav.addEventListener('click', (event) => {
        const link = event.target.closest('a[data-page]');
        if (!link) return;

        event.preventDefault();
        this.page = parseInt(link.dataset.page, 10);
        this.renderPage(list, true);
      });

      productList.insertAdjacentElement('afterend', pagination);
    }

    restore(list) {
      const empty = new Set();

      if (this.originalCards) {
        this.originalCards.forEach((card) => this.updateBadges(card, empty));
        this.showCards(list, this.originalCards);
      }

      this.orderedCards = null;
      this.page = 1;
      this.removePagination(list);
      this.setLoading(list, false);
      this.setThemePaginationHidden(false);
      this.setCounter(null, 0);
      this.setProductCount(null);
    }
  }

  customElements.define('sh-compatible-filter', ShCompatibleFilter);
})();
